import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import {
  tableSlotNodes,
  walkLayoutStructureChildren,
} from './layoutStructureChildLists';
import type {
  LayoutInlineStyle,
  LayoutNode,
  TableNode,
  TableRowEntry,
} from './types';

export type LayoutPlainCssHiddenQid = {
  qid: string;
  reason: string;
};

const HIDING_CLASS_TOKENS = new Set(['hidden', 'invisible', 'opacity-0']);

type CssHidingReason = {
  field: string;
  value: string;
};

function normalizeCssValue(value: string | number | undefined): string {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

function classTokens(className: string | undefined): string[] {
  if (!className?.trim()) return [];
  return className.trim().split(/\s+/);
}

function formatCssHidingReason(reason: CssHidingReason): string {
  return `${reason.field} "${reason.value}"`;
}

function hidingReasonFromStyle(
  style: LayoutInlineStyle,
  fieldPrefix = 'style'
): CssHidingReason | null {
  const display = normalizeCssValue(style.display);
  if (display === 'none') {
    return { field: `${fieldPrefix}.display`, value: 'none' };
  }

  const visibility = normalizeCssValue(style.visibility);
  if (visibility === 'hidden') {
    return { field: `${fieldPrefix}.visibility`, value: 'hidden' };
  }

  const opacity = normalizeCssValue(style.opacity);
  if (opacity === '0' || opacity === '0.0') {
    return { field: `${fieldPrefix}.opacity`, value: '0' };
  }

  return null;
}

function hidingReasonFromClassName(
  className: string | undefined,
  fieldName = 'className'
): CssHidingReason | null {
  for (const token of classTokens(className)) {
    if (HIDING_CLASS_TOKENS.has(token)) {
      return { field: fieldName, value: token };
    }
  }
  return null;
}

function hidingReasonFromNode(node: LayoutNode): string | null {
  const styleReason =
    'style' in node && node.style ? hidingReasonFromStyle(node.style) : null;
  if (styleReason) return formatCssHidingReason(styleReason);

  const classNameReason =
    'className' in node ? hidingReasonFromClassName(node.className) : null;
  if (classNameReason) return formatCssHidingReason(classNameReason);

  if (node.type === 'OASISMultiselect') {
    const checklistStyleReason = node.checklistStyle
      ? hidingReasonFromStyle(node.checklistStyle, 'checklistStyle')
      : null;
    if (checklistStyleReason) {
      return formatCssHidingReason(checklistStyleReason);
    }

    const checklistClassReason = hidingReasonFromClassName(
      node.checklistClassName,
      'checklistClassName'
    );
    if (checklistClassReason) {
      return formatCssHidingReason(checklistClassReason);
    }
  }

  if (node.type === 'table') {
    const tableStyleReason = hidingReasonFromTableStyles(node);
    if (tableStyleReason) return formatCssHidingReason(tableStyleReason);
  }

  const extraClassNames = collectExtraClassNames(node);
  for (const className of extraClassNames) {
    const reason = hidingReasonFromClassName(className);
    if (reason) return formatCssHidingReason(reason);
  }

  return null;
}

function firstHidingStyleReason(
  slots: Array<[LayoutInlineStyle | undefined, string]>
): CssHidingReason | null {
  for (const [style, fieldName] of slots) {
    if (!style) continue;
    const reason = hidingReasonFromStyle(style, fieldName);
    if (reason) return reason;
  }
  return null;
}

/**
 * Table-level inline styles that hide the whole table — mirrors the `'table'`
 * entry in `collectExtraClassNames`. The per-slot styles (`rowStyle`,
 * `cellStyle`, the header and row-label slots) each reach only one kind of
 * cell, so they are applied where that slot is walked rather than folded into
 * a reason every field under the table would inherit.
 */
function hidingReasonFromTableStyles(node: TableNode): CssHidingReason | null {
  return firstHidingStyleReason([[node.tableStyle, 'tableStyle']]);
}

/** First hide reason across inline-style slots, then className slots. */
function firstHidingSlotReason(
  styleSlots: Array<[LayoutInlineStyle | undefined, string]>,
  classSlots: Array<[string | undefined, string]>
): string | null {
  const styleReason = firstHidingStyleReason(styleSlots);
  if (styleReason) return formatCssHidingReason(styleReason);

  for (const [className, fieldName] of classSlots) {
    const reason = hidingReasonFromClassName(className, fieldName);
    if (reason) return formatCssHidingReason(reason);
  }
  return null;
}

/** `<th>` styling for the row-label column header (top-left corner cell). */
function hidingReasonFromRowHeader(node: TableNode): string | null {
  return firstHidingSlotReason(
    [[node.rowHeaderStyle, 'rowHeaderStyle']],
    [[node.rowHeaderClassName, 'rowHeaderClassName']]
  );
}

/** `<th>` styling shared by every answer column header. */
function hidingReasonFromColHeader(node: TableNode): string | null {
  return firstHidingSlotReason(
    [[node.colHeaderStyle, 'colHeaderStyle']],
    [[node.colHeaderClassName, 'colHeaderClassName']]
  );
}

/**
 * `<tr>` styling merged in `Table.tsx` — hides the whole row, label cell and
 * answer cells alike.
 */
function hidingReasonFromTableRowChrome(
  node: TableNode,
  row: TableRowEntry,
  rowIndex: number
): string | null {
  const prefix = `rows[${rowIndex}]`;
  return firstHidingSlotReason(
    [
      [node.rowStyle, 'rowStyle'],
      [row.rowStyle, `${prefix}.rowStyle`],
    ],
    [
      [node.rowClassName, 'rowClassName'],
      [row.rowClassName, `${prefix}.rowClassName`],
    ]
  );
}

/** Answer `<td>` styling — `Table.tsx` applies this to the cells, not the label. */
function hidingReasonFromTableRowCells(
  node: TableNode,
  row: TableRowEntry,
  rowIndex: number
): string | null {
  const prefix = `rows[${rowIndex}]`;
  return firstHidingSlotReason(
    [
      [node.cellStyle, 'cellStyle'],
      [row.cellStyle, `${prefix}.cellStyle`],
    ],
    [
      [node.cellClassName, 'cellClassName'],
      [row.cellClassName, `${prefix}.cellClassName`],
    ]
  );
}

/** Row-label `<td>` styling — hides the stub cell only, not the answers. */
function hidingReasonFromTableRowLabel(
  node: TableNode,
  row: TableRowEntry,
  rowIndex: number
): string | null {
  const prefix = `rows[${rowIndex}]`;
  return firstHidingSlotReason(
    [
      [node.rowLabelStyle, 'rowLabelStyle'],
      [row.rowLabelStyle, `${prefix}.rowLabelStyle`],
    ],
    [
      [node.rowLabelClassName, 'rowLabelClassName'],
      [row.rowLabelClassName, `${prefix}.rowLabelClassName`],
    ]
  );
}

function collectExtraClassNames(node: LayoutNode): Array<string | undefined> {
  switch (node.type) {
    case 'OASISRadio':
      return [
        node.innerClassName,
        node.labelClassName,
        node.groupClassName,
        node.optionRowClassName,
        node.optionLabelClassName,
        node.controlClassName,
        node.itemClassName,
      ];
    case 'OASISYesNoCheckbox':
      return [
        node.innerClassName,
        node.labelClassName,
        node.optionRowClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'OASISSelect':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.triggerClassName,
      ];
    case 'OASISText':
    case 'OASISDate':
    case 'OASISNumber':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.inputClassName,
      ];
    case 'OASISPhysician':
      return [
        node.innerClassName,
        node.labelClassName,
        node.headerClassName,
        node.actionsClassName,
        node.controlClassName,
        node.optionRowClassName,
        node.optionLabelClassName,
        node.detailsClassName,
        node.inputClassName,
      ];
    case 'OASISTime':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.inputClassName,
      ];
    case 'OASISMultiselect':
      return [
        node.innerClassName,
        node.labelClassName,
        node.checklistClassName,
        node.optionRowClassName,
        node.optionLabelClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'OASISMultiselectOption':
      return [
        node.className,
        node.labelClassName,
        node.optionRowClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'radio-option':
      return [node.className, node.controlClassName, node.itemClassName];
    case 'table':
      // Only the whole-table wrappers. The per-slot class names reach one kind
      // of cell each and are applied where that slot is walked.
      return [node.className, node.tableClassName];
    case 'card':
    case 'stack':
    case 'grid':
    case 'flex':
    case 'box':
    case 'indent':
    case 'field-row':
    case 'divider':
    case 'static':
    case 'text':
    case 'display':
    case 'external-answer':
    case 'section-label':
    case 'sum':
    case 'conditional':
    case 'disabled-when':
    case 'repeat':
      return [];
    default:
      return assertLayoutNodeExhaustive(node);
  }
}

function mergeHiddenQid(
  byQid: Map<string, LayoutPlainCssHiddenQid>,
  qid: string,
  reason: string
) {
  if (byQid.has(qid)) return;
  byQid.set(qid, { qid, reason });
}

/**
 * Required schema QIDs that sit under plain CSS hiding on the field node or an ancestor wrapper.
 *
 * Checks inline `style`, Tailwind `className` tokens, multiselect checklist props, and
 * table/row style slots for obvious hide patterns (`display:none`, `visibility:hidden`,
 * `opacity:0`, `hidden`, etc.).
 */
export function collectLayoutPlainCssHiddenQids(
  nodes: LayoutNode[],
  schemaQids: Iterable<string>
): LayoutPlainCssHiddenQid[] {
  const schemaQidSet = new Set(schemaQids);
  const byQid = new Map<string, LayoutPlainCssHiddenQid>();

  const walk = (arr: LayoutNode[], inheritedHideReason: string | null) => {
    for (const node of arr) {
      const nodeReason = hidingReasonFromNode(node);
      const activeHideReason = nodeReason ?? inheritedHideReason;

      const recordQid = (qid: string) => {
        if (!schemaQidSet.has(qid) || activeHideReason == null) return;
        mergeHiddenQid(byQid, qid, activeHideReason);
      };

      switch (node.type) {
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'divider':
        case 'static':
        case 'text':
        case 'display':
        case 'external-answer':
        case 'section-label':
        case 'sum':
        case 'table':
        case 'conditional':
        case 'disabled-when':
        case 'repeat':
        case 'OASISMultiselectOption':
          break;
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISPhysician':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
        case 'OASISMultiselect':
        case 'radio-option':
          recordQid(node.qid);
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }

      if (node.type === 'table') {
        // Each slot inherits the whole-table reason plus the styling that
        // actually reaches its own cell -- `Table.tsx` puts `cellClassName` on
        // the answer `<td>`s only, `rowLabelClassName` on the label `<td>`, and
        // the header class names on the `<th>`s, so folding them together would
        // report a field hidden that is plainly on screen.
        walk(
          tableSlotNodes(node.rowHeader),
          hidingReasonFromRowHeader(node) ?? activeHideReason
        );
        const colHeaderReason = hidingReasonFromColHeader(node);
        for (const header of node.colHeaders ?? []) {
          walk(tableSlotNodes(header), colHeaderReason ?? activeHideReason);
        }
        for (let ri = 0; ri < node.rows.length; ri++) {
          const row = node.rows[ri]!;
          // `rowClassName` hides the whole `<tr>`, label cell included.
          const rowReason = hidingReasonFromTableRowChrome(node, row, ri);
          const labelReason =
            rowReason ?? hidingReasonFromTableRowLabel(node, row, ri);
          const cellsReason =
            rowReason ?? hidingReasonFromTableRowCells(node, row, ri);
          walk(tableSlotNodes(row.label), labelReason ?? activeHideReason);
          walk(row.cells, cellsReason ?? activeHideReason);
        }
      } else {
        walkLayoutStructureChildren(node, childList =>
          walk(childList, activeHideReason)
        );
      }
    }
  };

  walk(nodes, null);
  return [...byQid.values()].sort((a, b) => a.qid.localeCompare(b.qid));
}
